import {createPortal} from 'react-dom';
import {ModalHeaderActionsContext} from './ModalShell';
/**
 * RECEPTION-OS-BUILD-V1 -- PrintSheet: THE shared print rail.
 *
 * The blueprint GAP 2 component (RECEPTION-OS-REQUIREMENTS-DOSSIER S7):
 * promotes the #442 owner-home print precedent (OwnerHomePage PrintSheet --
 * a purpose-built, chrome-free view swapped in by a `?print=1` URL param,
 * then `window.print()` on the browser's default stylesheet) into ONE
 * registered primitive. First adopters: the front-desk receipt print and the
 * consent-form print (Q-03 + Q-DP-PRINT = B: bills, receipts, AND consent
 * forms print at the desk).
 *
 * THE PRINT-NEVER-REPEATS-MONEY LAW (R-G): printing is presentation over an
 * ALREADY-COMMITTED record -- this component takes rendered children and
 * never writes money. Billing callers may audit the print request; a print failure cannot repeat a
 * transaction (FS-29). Money inside a print sheet renders FULL PRECISION
 * (the FORMATTER LAW, billing-entry-view.ts -- compact notation is for
 * heroes, never for print).
 */

import AcButton from './AcButton';
import PageHeader from './PageHeader';
import { formatTimestampIst } from '@/lib/datetime-ist';
import type { BillingDocumentActivity } from '@/dashboard/api';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

/** An existing parent panel can provide its single title and Back control. */
export const PrintSheetHostContext = createContext(false);

export interface PrintSheetProps {
  readonly title: string;
  /** A fixed source document may supply its own title; Back/Print stay unchanged. */
  readonly documentProvidesTitle?: boolean;
  /** Receipt-only saved-document chrome; other print consumers stay unchanged. */
  readonly receiptActions?: React.ReactNode;
  readonly printLabel?: string;
  /** Sub lines under the title (e.g. clinic, IST generated-at stamp). */
  readonly subs?: readonly string[];
  /** Label for the back action (names the surface it returns to). */
  readonly backLabel: string;
  readonly onBack: () => void;
  readonly children: React.ReactNode;
  readonly testId?: string;
  /** Billing documents audit an explicit print request before opening the browser dialog. */
  readonly onBeforePrint?: () => Promise<unknown>;
  readonly printIdentity?: string;
  readonly onLoadActivity?: () => Promise<BillingDocumentActivity>;
}

export default function PrintSheet({
  title,
  documentProvidesTitle = false,
  receiptActions,
  printLabel = 'Print',
  subs,
  backLabel,
  onBack,
  children,
  testId,
  onBeforePrint,
  printIdentity,
  onLoadActivity,
}: PrintSheetProps): JSX.Element {
  const parentProvidesChrome = useContext(PrintSheetHostContext);
  const headerActionsHost = useContext(ModalHeaderActionsContext);
  const printing = useRef(false);
  const mounted = useRef(true);
  const currentIdentity = useRef(printIdentity);
  currentIdentity.current = printIdentity;
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const [activity, setActivity] = useState<BillingDocumentActivity | null>(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityPending, setActivityPending] = useState(false);
  const [activityError, setActivityError] = useState(false);
  const activityVersion = useRef(0);
  useEffect(() => {
    activityVersion.current++;
    setActivity(null); setActivityOpen(false); setActivityPending(false); setActivityError(false);
  }, [printIdentity]);
  async function loadActivity() {
    if (!onLoadActivity) return;
    const version = ++activityVersion.current;
    const identity = currentIdentity.current;
    setActivityOpen(true); setActivityPending(true); setActivityError(false);
    try {
      const result = await onLoadActivity();
      if (mounted.current && activityVersion.current === version && currentIdentity.current === identity) setActivity(result);
    } catch {
      if (mounted.current && activityVersion.current === version && currentIdentity.current === identity) setActivityError(true);
    } finally {
      if (mounted.current && activityVersion.current === version && currentIdentity.current === identity) setActivityPending(false);
    }
  }
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  async function print() {
    if (printing.current) return;
    printing.current = true;
    setPending(true);
    setError(false);
    const requestedIdentity = currentIdentity.current;
    try {
      if (onBeforePrint) await onBeforePrint();
      if (mounted.current && currentIdentity.current === requestedIdentity) {
        // Keep an already-open activity view current, without another background read.
        activityVersion.current++;
        setActivity(null); setActivityOpen(false); setActivityPending(false);
        window.print();
      }
    } catch {
      if (mounted.current && currentIdentity.current === requestedIdentity) setError(true);
    } finally {
      printing.current = false;
      if (mounted.current) setPending(false);
    }
  }
  const printButton = (
        <AcButton variant={receiptActions ? "primary" : "secondary"} onClick={print} disabled={pending}>
          {pending ? 'Preparing print...' : printLabel}
        </AcButton>
  );
  const activityButton = (
        <AcButton variant="ghost" aria-expanded={activityOpen} onClick={() => {
          if (activityOpen) { activityVersion.current++; setActivityOpen(false); setActivityPending(false); }
          else void loadActivity();
        }} disabled={pending}>Activity</AcButton>
  );
  return (
    <div className={receiptActions ? "ac-print-sheet ac-print-sheet--receipt" : "ac-print-sheet"} data-testid={testId}>
      {headerActionsHost ? createPortal(printButton,headerActionsHost) : null}
      {documentProvidesTitle || parentProvidesChrome || headerActionsHost ? null : <PageHeader title={title} subs={subs} />}
      {receiptActions ? <div className="ac-print-sheet__paper">{children}</div> : children}
      <div className="ac-opsrow ac-print-sheet__actions">
        {receiptActions ? <><span className="ac-print-sheet__assurance">Printing does not change the payment.</span>{receiptActions}{onLoadActivity ? activityButton : null}</> : null}
        {error ? <p role="alert">Could not prepare printing. Please try again.</p> : null}
        {!headerActionsHost ? printButton : null}
        {parentProvidesChrome ? null : <AcButton variant="ghost" onClick={onBack} disabled={pending}>
          {backLabel}
        </AcButton>}
      </div>
      {onLoadActivity && (!receiptActions || activityOpen) ? <div className="ac-print-sheet__actions">
        {receiptActions ? null : activityButton}
        {activityOpen ? <section className="ac-section" aria-label="Document activity">
          <h2 className="ac-section__title">Document activity</h2>
          <p className="ac-section__sub">All times IST. Activity stays in the app.</p>
          {activityPending ? <p role="status">Loading activity...</p> : activityError ? <>
            <p role="alert">Could not load activity.</p>
            <AcButton variant="secondary" onClick={loadActivity}>Try again</AcButton>
          </> : activity ? <>
            {activity.events.length ? <ol>
              {activity.events.map((event, index) => <li key={`${event.at}:${index}`}>
                <strong>{event.action}</strong> · {event.actor}<br />
                <span>{event.at ? formatTimestampIst(event.at) : 'Time not recorded'}</span>
              </li>)}
            </ol> : <p>No recorded activity is available.</p>}
            {activity.hasMore ? <p>Showing the latest 50 entries.</p> : null}
            <p className="ac-section__sub">Print requested means the print dialog was requested. It does not confirm a paper copy.</p>
          </> : null}
        </section> : null}
      </div> : null}
    </div>
  );
}
